import { useEffect, useMemo, useState, type FormEvent } from "react";
import { Link, useSearchParams } from "react-router-dom";
import FilterGroup from "@/components/feature/FilterGroup";
import SortDropdown from "@/components/feature/SortDropdown";
import VideoGrid from "@/components/base/VideoGrid";
import Pagination from "@/components/base/Pagination";
import EmptyState from "@/components/base/EmptyState";
import {
  applyFilters,
  DEFAULT_FILTERS,
  GENRE_OPTIONS,
  getHot,
  paginate,
  REGION_OPTIONS,
  searchVideos,
  sortVideos,
  totalPages,
  YEAR_OPTIONS,
  type FilterState,
  type SortKey,
} from "@/lib/videoQuery";

const PAGE_SIZE = 12;

export default function SearchPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const keyword = searchParams.get("q") ?? "";

  const [inputValue, setInputValue] = useState(keyword);
  const [filters, setFilters] = useState<FilterState>(DEFAULT_FILTERS);
  const [sortKey, setSortKey] = useState<SortKey>("recommend");
  const [page, setPage] = useState(1);
  const [showFilters, setShowFilters] = useState(false);

  useEffect(() => {
    setInputValue(keyword);
    setFilters(DEFAULT_FILTERS);
    setSortKey("recommend");
    setPage(1);
  }, [keyword]);

  useEffect(() => {
    setPage(1);
  }, [filters, sortKey]);

  const matched = useMemo(() => searchVideos(keyword), [keyword]);
  const filtered = useMemo(() => applyFilters(matched, filters), [matched, filters]);
  const sorted = useMemo(() => sortVideos(filtered, sortKey), [filtered, sortKey]);
  const total = totalPages(sorted.length, PAGE_SIZE);
  const paged = paginate(sorted, page, PAGE_SIZE);
  const hot = getHot(6);

  const updateFilter = (key: keyof FilterState, value: string) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const q = inputValue.trim();
    setSearchParams(q ? { q } : {});
  };

  return (
    <div className="mx-auto max-w-site px-4 py-8 md:px-6 md:py-10">
      <nav className="mb-5 flex flex-wrap items-center gap-1.5 text-sm text-foreground-600" aria-label="面包屑导航">
        <Link to="/" className="transition-colors hover:text-primary-600">
          首页
        </Link>
        <i className="ri-arrow-right-s-line text-foreground-400" />
        <span className="text-foreground-900">搜索结果</span>
      </nav>

      <h1 className="font-heading text-2xl font-bold text-foreground-950 md:text-3xl">搜索内容</h1>

      <form
        onSubmit={handleSubmit}
        className="mt-5 flex flex-col gap-3 sm:flex-row sm:items-center"
      >
        <div className="flex flex-1 items-center gap-2 rounded-md border border-background-300 bg-background-50 px-3 py-2.5">
          <i className="ri-search-line text-lg text-foreground-500" />
          <input
            value={inputValue}
            onChange={(event) => setInputValue(event.target.value)}
            placeholder="搜索片名 / 主演 / 导演 / 类型"
            className="w-full bg-transparent text-sm text-foreground-900 placeholder:text-foreground-500 focus:outline-none"
          />
        </div>
        <button
          type="submit"
          className="whitespace-nowrap rounded-md bg-primary-600 px-6 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-700"
        >
          搜索
        </button>
      </form>

      {keyword ? (
        <>
          <div className="mt-5 flex flex-wrap items-center justify-between gap-3">
            <p className="text-sm text-foreground-600">
              关键词 “<span className="font-medium text-foreground-900">{keyword}</span>” 共找到{" "}
              <span className="font-semibold text-accent-600">{sorted.length}</span> 条结果
            </p>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => setShowFilters((prev) => !prev)}
                className="flex items-center gap-1.5 whitespace-nowrap rounded-md border border-background-300 px-3 py-1.5 text-sm font-medium text-foreground-700 transition-colors hover:border-primary-400 hover:text-primary-600"
              >
                <i className="ri-filter-3-line" />
                {showFilters ? "收起筛选" : "筛选"}
              </button>
              <SortDropdown value={sortKey} onChange={setSortKey} />
            </div>
          </div>

          {showFilters && (
            <div className="mt-4 space-y-6 rounded-lg border border-background-200 bg-background-100 p-5">
              <FilterGroup
                title="地区"
                options={REGION_OPTIONS}
                value={filters.region}
                onChange={(value) => updateFilter("region", value)}
              />
              <FilterGroup
                title="年份"
                options={YEAR_OPTIONS}
                value={filters.year}
                onChange={(value) => updateFilter("year", value)}
              />
              <FilterGroup
                title="类型"
                options={GENRE_OPTIONS}
                value={filters.genre}
                onChange={(value) => updateFilter("genre", value)}
              />
            </div>
          )}

          <div className="mt-6">
            {paged.length > 0 ? (
              <>
                <VideoGrid videos={paged} />
                <Pagination current={page} total={total} onChange={setPage} />
              </>
            ) : (
              <EmptyState
                icon="ri-emotion-sad-line"
                title="没有找到相关内容"
                description="换个关键词试试，或者看看下面这些热门内容推荐。"
                action={
                  <div className="flex flex-wrap justify-center gap-2">
                    {hot.slice(0, 4).map((item) => (
                      <Link
                        key={item.id}
                        to={`/detail/${item.id}`}
                        className="whitespace-nowrap rounded-md border border-background-300 px-3 py-1.5 text-xs font-medium text-foreground-700 transition-colors hover:border-primary-400 hover:text-primary-600"
                      >
                        {item.title}
                      </Link>
                    ))}
                  </div>
                }
              />
            )}
          </div>
        </>
      ) : (
        <div className="mt-6">
          <EmptyState
            icon="ri-search-line"
            title="输入关键词开始搜索"
            description="支持片名、主演、导演与类型匹配，例如「科幻」「张远」「悬疑」。"
          />
          <section className="mt-10">
            <h2 className="mb-4 font-heading text-lg font-bold text-foreground-950">大家都在看</h2>
            <VideoGrid videos={hot.slice(0, 4)} />
          </section>
        </div>
      )}
    </div>
  );
}